import axios from 'axios';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { BASE_URL, STORAGE_KEY_ACCESS_TOKEN, STORAGE_KEY_SELF_CREATED_CHAT, UNREG_CHATS_ID } from '../const';
import storageService from '../service/StorageService';
import ToastifyService, { TOAST_NOTIFY_TYPES } from '../service/ToastifyService';
import WSService from '../service/WSService';
import { clearAnonBirthDate, clearPendingBirthChart, readAnonBirthDate, readPendingBirthChart } from '../service/birthDate';
// HORO-10327: import from the *pure* service module — the component-
// level `tarotCredits.js` re-export pulls in authSlice via the
// `useTarotCredits` hook and would create a circular dependency
// (useAuth → hook module → authSlice → store → useAuth).
import { readAnonTarotCredits } from '../service/tarotCredits';
import { store } from '../store';
import {
    authUser,
    clearPendingAnonymousMigration,
    fetchUser,
    getPendingAnonymousMigration,
    setAuthToken,
    setUserDailyTarot,
    setUserMessagesLimits,
    toggleAuthModal,
    updateUser
} from '../store/slices/authSlice';
import { openBirthChartLoading } from '../store/slices/birthChartSlice';
import { selectBirthChartEnabled } from '../store/slices/featuresSlice';
import { detailsFromGenerateBody, toBirthDateTime } from '../utils/birthInfo';

// HORO-10504 bugfix: the anonymous → registered data migration — claiming
// pre-login chats, carrying over message usage, tarot usage, and birth
// date — used to live inline in the `jwt`-gated effect below, which only
// ever fires for the OAuth (Google/Facebook) redirect flow. Anyone who
// registered via email/password or OTP instead (the in-modal FirstAuthStep
// flow, which never redirects and never carries a `jwt` param) got none of
// it: their pre-login chat's `chat.user` was simply never set, so their
// very next message in that chat failed with "User not assigned to the
// chat". Extracted here so both flows can run the exact same bundle —
// the OAuth effect calls it directly; the email/OTP flow triggers it via
// the `pendingAnonymousMigration` flag effect further down.
const migrateAnonymousDataToAccount = async (dispatch) => {
    const unregisterChatIds = await storageService.get(UNREG_CHATS_ID);

    if (unregisterChatIds && unregisterChatIds.filter(Boolean).length) {
        const result = await dispatch(
            updateUser({
                chats: unregisterChatIds
            })
        );
        storageService.delete(UNREG_CHATS_ID);

        // If any chats were skipped (already owned by another user),
        // clear SELF_CREATED_CHAT if it points to a skipped chat
        // to prevent unauthorized access to another user's chat.
        const skippedChats = result?.payload?.skippedChats || [];
        if (skippedChats.length) {
            const selfCreated = storageService.get(STORAGE_KEY_SELF_CREATED_CHAT);
            if (selfCreated && skippedChats.includes(selfCreated)) {
                storageService.delete(STORAGE_KEY_SELF_CREATED_CHAT);
            }
        }
    }

    // Carry over pre-login message usage — backend counts actual
    // messages in DB for the merged chats (tamper-proof)
    if (unregisterChatIds?.length) {
        try {
            const token = storageService.get(STORAGE_KEY_ACCESS_TOKEN);
            const resp = await axios.post(
                `${BASE_URL}/user/carry-over-usage`,
                { chatIds: unregisterChatIds },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (resp.data?.limits) {
                dispatch(setUserMessagesLimits(resp.data.limits));
            }
        } catch (e) {
            console.error('[Auth] Failed to carry over usage:', e);
        }
    }

    // HORO-10327: forward anon tarot usage to the new account.
    // Backend merges with `max(anon.used, backend.used)` so
    // a user signing up mid-day can't reset their pool by
    // creating an account.
    //
    // The localStorage counter is deliberately left in place
    // rather than cleared. It used to be dropped here, on the
    // grounds that the server counter becomes the source of
    // truth once authenticated — which is true (the anon value
    // is only read when there is no user, and the
    // x-anon-tarot-credits header is only sent then too), but
    // it also meant signing in and straight back out handed the
    // browser a fresh pool. Keeping it costs nothing while
    // authenticated and closes that loop.
    //
    // Paid subscribers are still gated past this point (their
    // tarot reads count against the regular message-plan
    // quota), but the carry-over is harmless for them — the
    // counter sits dormant on the user doc until they drop
    // back to free.
    const anonTarot = readAnonTarotCredits();
    if (anonTarot.used > 0) {
        try {
            const token = storageService.get(STORAGE_KEY_ACCESS_TOKEN);
            const resp = await axios.post(
                `${BASE_URL}/user/carry-over-tarot`,
                { used: anonTarot.used, resetAt: anonTarot.resetAt },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (resp.data?.dailyTarot) {
                dispatch(setUserDailyTarot(resp.data.dailyTarot));
            }
        } catch (e) {
            console.error('[Auth] Failed to carry over tarot:', e);
        }
    }

    // HORO-10330: anon birth-date migration. If the user
    // entered a birth date as anonymous and their profile
    // doesn't already carry one, write it to the profile
    // via the existing updateUser endpoint. Profile-already-
    // set case discards the anon value (no overwrite). The
    // session-storage key is cleared either way.
    const anonBirthDate = readAnonBirthDate();
    if (anonBirthDate) {
        try {
            // Post-authUser store reflects the freshly-fetched
            // profile — safe to read birthDate from it.
            const currentBirthDate = store.getState().auth.user.birthDate;
            if (!currentBirthDate) {
                // HORO-10581: send a wall-clock DATE and no `birthTimeKnown`.
                //
                // The anon value comes from the horoscope flow, which only
                // ever collected a date — so absent is the truth ("never
                // asked") and a `false` would read as a decline, triggering
                // the noon convention for a question nobody put to them.
                //
                // The conversion matters too: the anon value is built by
                // `toIsoBirthDate`, i.e. LOCAL midnight, so its UTC components
                // carry the browser's offset. Passing the raw instant let the
                // server read that offset back as a birth time (07:00 for a
                // Pacific user). Converting here, where the browser's zone is
                // known, is the only place that can be done correctly.
                const anon = new Date(anonBirthDate);
                const birthDateTime = toBirthDateTime({
                    year: anon.getFullYear(),
                    month: anon.getMonth() + 1,
                    day: anon.getDate(),
                    timeKnown: false
                });
                await dispatch(updateUser({ birthDateTime }));
            }
        } catch (e) {
            console.error('[Auth] Failed to migrate anon birth date:', e);
        }
    }
    clearAnonBirthDate();

    // HORO-10360: an anonymous "Generate Birth Chart" stopped at the sign-up wall
    // with its details parked in sessionStorage. Now that the account exists,
    // reopen the flow at the loader — BirthChartFlow starts the request from
    // there, and lands on the chart view. Runs after the profile birth-date
    // migration above so the chart's own (authoritative) details win.
    const pendingBirthChart = readPendingBirthChart();
    if (pendingBirthChart) {
        clearPendingBirthChart();
        if (selectBirthChartEnabled(store.getState())) {
            dispatch(openBirthChartLoading({ details: detailsFromGenerateBody(pendingBirthChart) }));
        }
    }

    // The anonymous message tally is deliberately NOT deleted here,
    // for the same reason as the tarot counter above. Deleting it
    // made sign-in-then-out a free refill of the pre-login
    // allowance — and it also quietly defeated the logout handler,
    // which does try to carry the tally through its storage wipe but
    // by then had nothing left to carry.
    //
    // Keeping it is inert while authenticated: the tally is only
    // read on the anonymous branch of the send gate, only written
    // for anonymous users, and the header counter it feeds is only
    // rendered when there is no user (see ChatHeader). The
    // server-side carry-over is unaffected — it counts messages in
    // the merged chats, not this counter.
};

const useAuth = () => {
    const accessToken = storageService.get(STORAGE_KEY_ACCESS_TOKEN);

    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const jwt = searchParams.get('jwt');
    const error = searchParams.get('error');

    const clearSearchParams = useCallback(() => {
        if (searchParams.has('error')) {
            searchParams.delete('error');
        }
        searchParams.delete('jwt');
        setSearchParams(searchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        (async () => {
            // Long-standing bug (blame: 0a0db2c8f, 2023-06-28, predates this
            // ticket): `searchParams.get('error')` returns JS `null` — no
            // quotes — when the param is simply absent, which is true for
            // almost every page load in the app. Comparing that against the
            // STRING 'null' meant `error !== 'null'` was true for essentially
            // every navigation, not just genuine OAuth-redirect errors —
            // firing an (empty-message) error toast and, worse, an
            // unconditional `clearSearchParams()` replace-navigation on
            // mount. That replace races the mount-time effects of any
            // `<Navigate>` rendered as the matched route element on a fresh
            // page load (e.g. CatalogPageV2's /catalog/:category redirect,
            // SubscriptionPage's/ProfileV2's own already-subscribed
            // redirects) — both fire in the same commit, and whichever
            // settles last wins, so this could silently strand a visitor on
            // the pre-redirect URL depending on effect-ordering. `error` now
            // has to be a real, present value — not just anything other than
            // the literal string 'null' — before treating it as an error.
            if (error && error !== 'null') {
                ToastifyService.setToast(error, TOAST_NOTIFY_TYPES.ERROR, { autoClose: 5000 });
                clearSearchParams();
                return;
            }
            if (!jwt) {
                return;
            }
            await storageService.add(STORAGE_KEY_ACCESS_TOKEN, jwt.toString());
            dispatch(authUser()).then(async () => {
                // HORO-10509: "enforce sharing of email" lands here — Google
                // always returns one, but Facebook lets the user decline the
                // permission even after it's requested (see
                // FacebookAuthProvider's scope). A brand-new OAuth account
                // with no email is otherwise fully usable, so this is the
                // one place that catches it and routes into the add-email
                // step rather than silently letting the account go without.
                if (!store.getState().auth.user?.email) {
                    dispatch(toggleAuthModal({ trigger: true, step: 1, flow: 'add-email' }));
                }

                await migrateAnonymousDataToAccount(dispatch);
            });
            clearSearchParams();
            await WSService.init();
        })();
    }, [clearSearchParams, dispatch, jwt]);

    // HORO-10504 bugfix: the in-modal email/OTP sign-up and sign-in flow
    // (FirstAuthStep) never redirects and never carries a `jwt` param, so
    // the effect above never fires for it. applyAuthenticatedUser sets this
    // flag instead — run the same migration bundle here, then clear it so
    // it doesn't re-run.
    const pendingAnonymousMigration = useSelector(getPendingAnonymousMigration);
    useEffect(() => {
        if (!pendingAnonymousMigration) {
            return;
        }

        (async () => {
            await migrateAnonymousDataToAccount(dispatch);
            dispatch(clearPendingAnonymousMigration());
            await WSService.init();
        })();
    }, [pendingAnonymousMigration, dispatch]);

    useEffect(() => {
        if (!accessToken || jwt) {
            return;
        }

        (async () => {
            dispatch(setAuthToken(accessToken));
            dispatch(fetchUser());

            await WSService.init();
        })();
    }, [accessToken, dispatch, jwt]);
};
export default useAuth;
